import React, { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import DownloadApp from '@/components/shared/DownloadApp';
import { 
  ShoppingCart,
  Calendar,
  Users,
  Package,
  FileText,
  CreditCard,
  Bell,
  Smartphone,
  Clock,
  CheckCircle,
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Shield,
  BarChart3,
  MessageSquare,
  Upload,
  UserCheck,
  AlertCircle,
  Car,
  FileCheck,
  ClipboardList,
  Headphones,
  RefreshCw,
  Percent,
  MapPin,
  Send,
  PhoneCall,
  FileSignature,
  Building2,
  CheckSquare,
  Timer
} from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faMobile, faUser, faStickyNote, faBuilding } from '@fortawesome/free-solid-svg-icons';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  benefits: string[];
}

interface SolutionOption {
  title: string;
  description: string;
  features: string[];
  cta: string;
  ctaLink: string;
  highlighted?: boolean;
}

const QueueForYouSoftwarePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    businessName: '',
    interest: 'whitelabel',
    subject: '',
    message: ''
  });

  const features: Feature[] = [
    {
      icon: <MessageSquare className="w-12 h-12" />,
      title: "In-App Chat Service Requests",
      description: "Customers submit any queue service via mobile chat",
      benefits: [
        "24/7 service request submission",
        "Real-time chat with agents",
        "Multi-service request handling",
        "Chat history & tracking",
        "Automated acknowledgment",
        "Smart service categorization",
        "Priority queue management",
        "Template responses for common queries"
      ]
    },
    {
      icon: <Upload className="w-12 h-12" />,
      title: "Document Upload & Management",
      description: "Secure document submission and storage for all services",
      benefits: [
        "ID document uploads",
        "Vehicle registration papers",
        "Proof of address documents",
        "Traffic fine documentation",
        "Multiple file format support (PDF, JPG, PNG)",
        "Document verification status",
        "Secure encrypted storage",
        "Easy document retrieval"
      ]
    },
    {
      icon: <UserCheck className="w-12 h-12" />,
      title: "Agent Assignment & Communication",
      description: "Dedicated agents handle customer requests with full liaison",
      benefits: [
        "Automatic agent assignment",
        "Agent availability status",
        "Direct agent-customer chat",
        "Agent performance tracking",
        "Escalation management",
        "Agent workload balancing",
        "Multi-language support",
        "Agent expertise matching"
      ]
    },
    {
      icon: <Bell className="w-12 h-12" />,
      title: "Real-Time Service Updates",
      description: "Keep customers informed at every stage of the process",
      benefits: [
        "Push notifications for status changes",
        "Email & SMS updates",
        "Document submission confirmations",
        "Queue position tracking",
        "Processing stage notifications",
        "Completion alerts",
        "Payment reminders",
        "Collection/delivery updates"
      ]
    },
    {
      icon: <RefreshCw className="w-12 h-12" />,
      title: "License Renewal Services",
      description: "Automated reminders and seamless license renewal processing",
      benefits: [
        "Driver's license renewal reminders",
        "Vehicle license disk renewals",
        "90-day, 60-day, 30-day alerts",
        "Expiry date tracking",
        "Renewal fee calculations",
        "Online renewal submissions",
        "Document collection scheduling",
        "Bulk renewal for fleets"
      ]
    },
    {
      icon: <Percent className="w-12 h-12" />,
      title: "Traffic Fine Management",
      description: "Discounted traffic fine payments and dispute handling",
      benefits: [
        "Traffic fine checking & tracking",
        "Discount negotiations with authorities",
        "Payment plan options",
        "Fine dispute assistance",
        "Outstanding fine alerts",
        "Fine history tracking",
        "Warrant of arrest checks",
        "Clearance certificate generation"
      ]
    },
    {
      icon: <Car className="w-12 h-12" />,
      title: "Vehicle Change of Ownership",
      description: "Complete vehicle transfer and registration services",
      benefits: [
        "Buyer & seller document verification",
        "Roadworthy certificate coordination",
        "Transfer duty calculations",
        "eNaTIS submission handling",
        "Registration & licensing",
        "Number plate ordering",
        "Status tracking dashboard",
        "Digital completion certificates"
      ]
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Complete CRM System",
      description: "Comprehensive customer relationship management",
      benefits: [
        "Customer profile database",
        "Service history tracking",
        "Vehicle & license information",
        "Communication history logs",
        "Renewal date management",
        "Preferred service tracking",
        "Customer segmentation",
        "Automated follow-ups"
      ]
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: "Invoicing & Payment Processing",
      description: "Professional invoicing with multiple payment options",
      benefits: [
        "Instant quote generation",
        "Itemized invoicing",
        "Service package pricing",
        "Discount code management",
        "Tax-compliant invoices",
        "Payment tracking",
        "Refund management",
        "Receipt automation"
      ]
    },
    {
      icon: <CreditCard className="w-12 h-12" />,
      title: "Online Payment Integration",
      description: "Secure online payments for all services",
      benefits: [
        "Credit/Debit card payments",
        "Instant EFT",
        "Mobile wallet support",
        "Split payment options",
        "Secure payment gateway",
        "Payment confirmation emails",
        "Transaction history",
        "Automated receipts"
      ]
    },
    {
      icon: <ClipboardList className="w-12 h-12" />,
      title: "Service Request Tracking",
      description: "Track multiple service requests from submission to completion",
      benefits: [
        "Real-time status dashboard",
        "Stage-by-stage tracking",
        "Estimated completion dates",
        "Document checklist status",
        "Agent activity logs",
        "Service timeline view",
        "Historical request archive",
        "Performance metrics"
      ]
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Analytics & Business Intelligence",
      description: "Data-driven insights to grow your queue business",
      benefits: [
        "Revenue & profit tracking",
        "Service type analysis",
        "Customer acquisition metrics",
        "Agent performance reports",
        "Turnaround time analytics",
        "Customer satisfaction scores",
        "Peak hour identification",
        "Custom report builder"
      ]
    }
  ];

  const solutionOptions: SolutionOption[] = [
    {
      title: "Join CarSparesEzyFind",
      description: "List your Queue For You services on our platform",
      features: [
        "Access to thousands of customers",
        "Website + Android, iPhone & Huawei apps",
        "In-app chat & service requests",
        "Integrated payment processing",
        "Marketing & SEO support",
        "No setup fees - Start immediately",
        "24/7 technical support",
        "Analytics & sales dashboard"
      ],
      cta: "Join the Platform",
      ctaLink: "https://www.CarSparesEzyFind.co.za/list-business"
    },
    {
      title: "WhiteLabel Solution",
      description: "Your own branded Queue For You platform",
      features: [
        "Fully branded website & mobile apps",
        "Android, iPhone & Huawei native apps",
        "Complete queue management system",
        "All features listed above included",
        "Your own domain & branding",
        "Launch your business tomorrow!",
        "Unlimited customers & agents",
        "Dedicated account manager"
      ],
      cta: "Start Tomorrow",
      ctaLink: "#whitelabel-quote",
      highlighted: true
    }
  ];

  const serviceStages = [
    {
      stage: "Service Request Submitted",
      icon: <MessageSquare className="w-6 h-6" />,
      notification: "Chat request received: 'License renewal for John Doe' - Ticket #12345"
    },
    {
      stage: "Documents Uploaded",
      icon: <Upload className="w-6 h-6" />,
      notification: "Push notification: 'Documents received successfully - Under review'"
    },
    {
      stage: "Agent Assigned",
      icon: <UserCheck className="w-6 h-6" />,
      notification: "Email: 'Sarah from our team will handle your request - Chat now available'"
    },
    {
      stage: "Document Verification",
      icon: <FileCheck className="w-6 h-6" />,
      notification: "SMS: 'Documents verified ✓ - Processing your license renewal'"
    },
    {
      stage: "Queue Position Update",
      icon: <Timer className="w-6 h-6" />,
      notification: "App notification: 'You're #3 in queue - Est. completion: 2 hours'"
    },
    {
      stage: "Processing at Department",
      icon: <Building2 className="w-6 h-6" />,
      notification: "Real-time update: 'Agent is at traffic department on your behalf'"
    },
    {
      stage: "Payment Required",
      icon: <CreditCard className="w-6 h-6" />,
      notification: "Payment request: 'R450 license fee + R150 service fee - Pay now'"
    },
    {
      stage: "Service Completed",
      icon: <CheckCircle className="w-6 h-6" />,
      notification: "Success! 'Your license disk is ready - Collection/delivery options'"
    },
    {
      stage: "Feedback Request",
      icon: <Star className="w-6 h-6" />,
      notification: "Thank you message with rating request & digital receipt"
    }
  ];

  const servicesOffered = [
    {
      icon: <RefreshCw className="w-10 h-10" />,
      title: "License Renewals",
      services: ["Driver's License", "Vehicle License Disk", "PDP Permits", "Professional Licenses"]
    },
    {
      icon: <Car className="w-10 h-10" />,
      title: "Vehicle Services",
      services: ["Change of Ownership", "Registration", "Roadworthy Bookings", "Number Plates"]
    },
    {
      icon: <Percent className="w-10 h-10" />,
      title: "Traffic Fines",
      services: ["Fine Payments", "Discount Negotiations", "Dispute Handling", "Clearance Certificates"]
    },
    {
      icon: <FileSignature className="w-10 h-10" />,
      title: "Documentation",
      services: ["ID Applications", "Police Clearance", "Affidavits", "Document Certification"]
    }
  ];

  const stats = [
    { value: "500+", label: "Queue Businesses" },
    { value: "50K+", label: "Monthly Services" },
    { value: "98%", label: "Success Rate" },
    { value: "24hrs", label: "Launch Time" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    alert('Thank you! We will contact you within 24 hours. Your business can be ready tomorrow!');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <>
      <Head>
        <title>Queue For You Software - Start Your Queue Business Tomorrow | CarSparesEzyFind</title>
        <meta name="description" content="Complete Queue For You software with in-app chat, document uploads, real-time updates, license renewals, traffic fines, CRM, and mobile apps. Launch your queue business tomorrow!" />
        <meta name="keywords" content="queue for you software, license renewal service, traffic fine management, vehicle transfer service, queue management system, agent liaison software" />
      </Head>

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 text-white py-20 overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-20"></div>
          <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <Zap className="w-4 h-4 mr-2 text-yellow-300" />
                <span className="text-sm font-semibold">Launch Your Queue Business Tomorrow!</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Start Your <span className="text-yellow-300">"Queue For You"</span> Business
              </h1>
              
              <p className="text-xl md:text-2xl mb-4 text-purple-100">
                Complete Software Solution to Run Your Queue Services Business
              </p>
              
              <p className="text-lg mb-8 text-purple-200">
                In-App Chat • Document Uploads • Agent Liaison • Real-Time Updates • License Renewals • Traffic Fines
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <a 
                  href="https://www.CarSparesEzyFind.co.za/list-business" 
                  className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center"
                >
                  Join Our Platform
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
                <a 
                  href="#whitelabel-quote" 
                  className="bg-white hover:bg-gray-100 text-purple-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl flex items-center justify-center border-2 border-white"
                >
                  Start Your Business Tomorrow
                  <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                {stats.map((stat, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-3xl font-bold text-yellow-300">{stat.value}</div>
                    <div className="text-sm text-purple-100 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Quick Launch Banner */}
        <section className="py-12 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-8 text-center md:text-left">
              <div className="flex items-center gap-4">
                <Zap className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Ready in 24 Hours</h3>
                  <p className="text-green-100">Set up today, launch tomorrow</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <Smartphone className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Mobile Apps</h3>
                  <p className="text-green-100">Android, iPhone & Huawei</p>
                </div>
              </div>
              <div className="hidden md:block w-px h-16 bg-white/30"></div>
              <div className="flex items-center gap-4">
                <CheckCircle className="w-16 h-16 text-yellow-300" />
                <div>
                  <h3 className="text-2xl font-bold mb-1">Complete System</h3>
                  <p className="text-green-100">Everything you need included</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Services You Can Offer
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Handle any government queue service through your branded platform
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              {servicesOffered.map((service, index) => (
                <div key={index} className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border-2 border-indigo-200 hover:shadow-lg transition-all">
                  <div className="text-indigo-600 mb-4 flex justify-center">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">{service.title}</h3>
                  <ul className="space-y-2">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-center text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Everything You Need to Run Your Business
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional queue management system with all the features you need
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl p-8 hover:shadow-2xl transition-all hover:border-indigo-600 transform hover:-translate-y-2"
                >
                  <div className="text-indigo-600 mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Chat & Communication Highlight */}
        <section className="py-20 bg-gradient-to-br from-purple-900 to-indigo-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <MessageSquare className="w-16 h-16 text-yellow-300 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  In-App Chat & Agent Liaison
                </h2>
                <p className="text-xl text-purple-200">
                  Customers chat directly with agents for any queue service request
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: <MessageSquare className="w-8 h-8" />, title: "Live Chat", desc: "Real-time agent-customer chat" },
                  { icon: <Upload className="w-8 h-8" />, title: "Document Upload", desc: "Submit docs via chat interface" },
                  { icon: <Bell className="w-8 h-8" />, title: "Instant Notifications", desc: "Push, email & SMS alerts" },
                  { icon: <UserCheck className="w-8 h-8" />, title: "Agent Assignment", desc: "Dedicated agent per request" },
                  { icon: <Clock className="w-8 h-8" />, title: "Status Updates", desc: "Real-time progress tracking" },
                  { icon: <FileCheck className="w-8 h-8" />, title: "Document Verification", desc: "Instant doc approval/rejection" },
                  { icon: <PhoneCall className="w-8 h-8" />, title: "Voice Calls", desc: "In-app calling capability" },
                  { icon: <Star className="w-8 h-8" />, title: "Service Rating", desc: "Customer feedback & reviews" }
                ].map((item, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all">
                    <div className="text-yellow-300 flex justify-center mb-3">
                      {item.icon}
                    </div>
                    <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                    <p className="text-purple-200 text-sm">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Service Journey Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Bell className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Customer Journey with Real-Time Updates
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Customers stay informed at every stage of their service request
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-indigo-500 to-purple-600 hidden md:block"></div>

                {serviceStages.map((stage, index) => (
                  <div key={index} className="relative mb-8 md:ml-20">
                    <div className="absolute -left-20 top-2 hidden md:block">
                      <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center text-white shadow-lg">
                        {stage.icon}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-indigo-600 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="md:hidden text-indigo-600">{stage.icon}</div>
                        <div className="flex-1">
                          <h4 className="text-xl font-bold text-gray-900 mb-2">{stage.stage}</h4>
                          <div className="flex items-start gap-2">
                            <Bell className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                            <p className="text-gray-600">{stage.notification}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* License Renewal Reminders */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <RefreshCw className="w-16 h-16 text-indigo-600 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  Automated License Renewal Reminders
                </h2>
                <p className="text-xl text-gray-600">
                  Never miss a renewal - automated reminders keep customers coming back
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { 
                    icon: <AlertCircle className="w-12 h-12" />, 
                    title: "90-Day Alert", 
                    desc: "First reminder 90 days before expiry",
                    color: "text-yellow-600 bg-yellow-50 border-yellow-200"
                  },
                  { 
                    icon: <Bell className="w-12 h-12" />, 
                    title: "60-Day Alert", 
                    desc: "Second reminder with service offer",
                    color: "text-orange-600 bg-orange-50 border-orange-200"
                  },
                  { 
                    icon: <AlertCircle className="w-12 h-12" />, 
                    title: "30-Day Alert", 
                    desc: "Urgent reminder with priority booking",
                    color: "text-red-600 bg-red-50 border-red-200"
                  }
                ].map((item, index) => (
                  <div key={index} className={`rounded-xl p-8 border-2 ${item.color} text-center`}>
                    <div className="flex justify-center mb-4">
                      {item.icon}
                    </div>
                    <h4 className="text-xl font-bold mb-2">{item.title}</h4>
                    <p className="text-gray-700">{item.desc}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-3">Automated Revenue Stream</h3>
                <p className="text-lg">Renewal reminders automatically bring customers back for repeat business year after year!</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Options Section */}
        <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Choose Your Solution
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Join our platform or launch your own branded queue business tomorrow
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {solutionOptions.map((option, index) => (
                <div 
                  key={index}
                  className={`relative rounded-2xl p-8 ${
                    option.highlighted 
                      ? 'bg-gradient-to-br from-indigo-600 to-purple-700 text-white shadow-2xl transform scale-105' 
                      : 'bg-white border-2 border-gray-200'
                  }`}
                >
                  {option.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-yellow-400 text-gray-900 px-4 py-1 rounded-full text-sm font-bold">
                        START TOMORROW!
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    <h3 className={`text-3xl font-bold mb-2 ${option.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {option.title}
                    </h3>
                    <p className={option.highlighted ? 'text-purple-100' : 'text-gray-600'}>
                      {option.description}
                    </p>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {option.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle className={`w-5 h-5 mr-3 mt-0.5 flex-shrink-0 ${
                          option.highlighted ? 'text-yellow-300' : 'text-green-500'
                        }`} />
                        <span className={option.highlighted ? 'text-purple-50' : 'text-gray-700'}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a 
                    href={option.ctaLink}
                    className={`block text-center px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 ${
                      option.highlighted
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-gray-900'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {option.cta}
                    <ArrowRight className="inline-block ml-2 w-5 h-5" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WhiteLabel Quote Section */}
        <section id="whitelabel-quote" className="py-20 bg-gradient-to-br from-gray-900 to-indigo-900 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <Zap className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-4xl md:text-5xl font-bold mb-4">
                  Launch Your Queue Business Tomorrow
                </h2>
                <p className="text-xl text-indigo-100">
                  Complete branded platform ready in 24 hours - Start earning immediately!
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-12">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                    What's Included
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Fully branded website with your logo",
                      "Android, iPhone & Huawei mobile apps",
                      "In-app chat & messaging system",
                      "Document upload & management",
                      "Agent assignment & liaison tools",
                      "Real-time notification system",
                      "License renewal reminder automation",
                      "Traffic fine discount management",
                      "Vehicle transfer workflow",
                      "Complete CRM system",
                      "Invoicing & payment processing",
                      "Push, email & SMS notifications",
                      "Analytics & reporting dashboard",
                      "Your own domain name"
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <ArrowRight className="w-5 h-5 text-yellow-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                  <h3 className="text-2xl font-bold mb-4 flex items-center">
                    <Timer className="w-6 h-6 text-blue-400 mr-2" />
                    24-Hour Launch Process
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-indigo-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">1</div>
                      <div>
                        <h4 className="font-bold mb-1">Submit Form Today</h4>
                        <p className="text-indigo-100 text-sm">Fill out the form below with your business details</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-indigo-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">2</div>
                      <div>
                        <h4 className="font-bold mb-1">We Set Up Everything</h4>
                        <p className="text-indigo-100 text-sm">We configure your platform overnight</p>
                      </div>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-indigo-500 rounded-full w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0 font-bold">3</div>
                      <div>
                        <h4 className="font-bold mb-1">Launch Tomorrow!</h4>
                        <p className="text-indigo-100 text-sm">Start accepting customers and earning revenue</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-yellow-400/20 border-2 border-yellow-400 rounded-lg">
                    <p className="font-bold text-yellow-300">
                      ⚡ Limited Offer: First 20 businesses get 50% off setup!
                    </p>
                  </div>

                  <div className="mt-6 p-4 bg-green-500/20 border-2 border-green-400 rounded-lg">
                    <p className="font-bold text-green-300 text-center">
                      🚀 Launch Tomorrow & Start Earning Immediately!
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
                <h3 className="text-3xl font-bold text-center mb-8">Start Your Queue Business Tomorrow</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='name' 
                        placeholder='Enter name' 
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-indigo-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faUser} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='flex flex-col md:flex-row md:gap-5'>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Phone *</label>
                      <div className='relative'>
                        <div className='flex'>
                          <input 
                            type='text' 
                            name='code' 
                            placeholder='27' 
                            value={'+27'} 
                            disabled 
                            className='form-control border border-gray-300 border-r-0 text-sm xl:text-[14px] 2xl:text-md h-10 px-3 w-[50px] rounded-l' 
                          />
                          <input 
                            type='text' 
                            name='phone' 
                            placeholder='Enter mobile number' 
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded-r focus:border-indigo-600 focus:outline-none' 
                          />
                        </div>
                        <FontAwesomeIcon icon={faMobile} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                    <div className='mb-4 w-full'>
                      <label className='mb-2 block font-semibold'>Email *</label>
                      <div className='relative'>
                        <input 
                          type='email' 
                          name='email' 
                          placeholder='Enter email Id' 
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-indigo-600 focus:outline-none' 
                        />
                        <FontAwesomeIcon icon={faEnvelope} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                      </div>
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Business Name *</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='businessName' 
                        placeholder='Enter your queue business name' 
                        value={formData.businessName}
                        onChange={handleChange}
                        required
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-indigo-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faBuilding} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>I'm interested in *</label>
                    <select
                      name='interest'
                      value={formData.interest}
                      onChange={handleChange}
                      className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-indigo-600 focus:outline-none'
                    >
                      <option value="whitelabel">WhiteLabel Solution - Launch Tomorrow!</option>
                      <option value="join">Joining CarSparesEzyFind</option>
                      <option value="both">Both Options - Need More Info</option>
                    </select>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Subject</label>
                    <div className='relative'>
                      <input 
                        type='text' 
                        name='subject' 
                        placeholder='Subject' 
                        value={formData.subject}
                        onChange={handleChange}
                        className='form-control border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full h-10 px-3 rounded focus:border-indigo-600 focus:outline-none' 
                      />
                      <FontAwesomeIcon icon={faStickyNote} className='absolute top-[50%] translate-y-[-50%] right-4 text-gray-500' />
                    </div>
                  </div>

                  <div className='mb-5'>
                    <label className='mb-2 block font-semibold'>Message</label>
                    <textarea 
                      name='message'
                      placeholder='Tell us about your queue business plans' 
                      rows={5} 
                      value={formData.message}
                      onChange={handleChange}
                      className='form-control py-3 border border-gray-300 text-sm xl:text-[14px] 2xl:text-md w-full px-3 rounded focus:border-indigo-600 focus:outline-none'
                    />
                  </div>

                  <div className='text-right'>
                    <button 
                      type="submit"
                      className='bg-indigo-600 text-white border border-indigo-600 uppercase transition-all hover:bg-white hover:text-indigo-600 px-10 py-3 rounded font-bold'
                    >
                      Launch My Business Tomorrow
                    </button>
                  </div>
                </form>

                <p className="text-center text-sm text-gray-600 mt-6">
                  🚀 Submit now and we'll have your platform ready in 24 hours!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-indigo-700 to-purple-800 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Ready to Start Your Queue Business?
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
              Launch tomorrow with a complete software solution - No technical knowledge required!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://www.CarSparesEzyFind.co.za/list-business"
                className="bg-white hover:bg-gray-100 text-indigo-700 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Join CarSparesEzyFind
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
              <a 
                href="#whitelabel-quote"
                className="bg-yellow-400 hover:bg-yellow-300 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center"
              >
                Launch Tomorrow 🚀
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
         <DownloadApp/>
    </>
  );
};

export default QueueForYouSoftwarePage;